import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const normalizeDateOnly = (value) => {
    if (!value || typeof value !== 'string') return value;
    if (value.includes('T')) return value.split('T')[0];
    return value;
};

const EventService = {
    getEvents: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/events`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching events:", error);
            throw error;
        }
    },
    addEvent: async (event, companyId, token, userType) => {
        try {
            const payload = { ...event, eventDate: normalizeDateOnly(event?.eventDate) };
            const response = await axios.post(`${API_URL}/events`, payload, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });

            return response.data;
        } catch (error) {
            console.error("Error adding event:", error);
            throw error;
        }
    },
    deleteEvent: async (id, companyId, token, userType) => {
        try {
            await axios.delete(`${API_URL}/events/${id}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
        } catch (error) {
            console.error("Error deleting event:", error);
            throw error;
        }
    },
    updateEvent: async (id, updatedEvent, companyId, token, userType) => {
        try {
            const payload = { ...updatedEvent, eventDate: normalizeDateOnly(updatedEvent?.eventDate) };
            const response = await axios.put(`${API_URL}/events/${id}`, payload, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });

            return response.data;
        } catch (error) {
            console.error("Error updating event:", error);
            throw error;
        }
    },
};

const EventForm = ({ onEventSaved, companyId, token, userType, editingEvent, onCancelEdit }) => {
    const [form, setForm] = useState({
        eventName: "",
        eventDate: "",
        description: "",
        isHoliday: false,
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (!editingEvent) {
            setForm({ eventName: "", eventDate: "", description: "", isHoliday: false });
            return;
        }

        const normalizedDate = editingEvent.eventDate
            ? new Date(editingEvent.eventDate).toISOString().slice(0, 10)
            : "";

        setForm({
            eventName: editingEvent.eventName || "",
            eventDate: normalizedDate,
            description: editingEvent.description || "",
            isHoliday: Boolean(editingEvent.isHoliday),
        });
        setError(null);
        setSuccess(null);
    }, [editingEvent]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingEvent?.id) {
                await EventService.updateEvent(editingEvent.id, form, companyId, token, userType);
                setSuccess("Event updated successfully!");
            } else {
                await EventService.addEvent(form, companyId, token, userType);
                setForm({ eventName: "", eventDate: "", description: "", isHoliday: false });
                setSuccess("Event added successfully!");
            }
            setError(null);
            onEventSaved();
        } catch (error) {
            setError(error.response?.data?.error || (editingEvent?.id ? "Failed to update event" : "Failed to add event"));
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto"
        >
            <h3 className="text-xl font-bold text-gray-700 mb-4">{editingEvent ? "Edit Event" : "Add Event"}</h3>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-4">
                    {error}
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-4">
                    {success}
                </div>
            )}

            <input
                name="eventName"
                placeholder="Event Name"
                value={form.eventName}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="relative mb-6">
                <input
                    id="eventDate"
                    type="date"
                    name="eventDate"
                    value={form.eventDate}
                    onChange={handleChange}
                    required
                    className="w-full p-3 pt-5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-transparent"
                    placeholder=" "
                />
                <label
                    htmlFor="eventDate"
                    className={`absolute left-3 top-3 text-gray-400 text-sm transition-all ${
                        form.eventDate ? "-translate-y-3 scale-75" : ""
                    }`}
                >
                    Event Date
                </label>
            </div>
            <textarea
                name="description"
                placeholder="Event Description"
                value={form.description}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <label className="block mb-2 font-semibold">Is it a Holiday?</label>
            <input
                type="checkbox"
                name="isHoliday"
                checked={form.isHoliday}
                onChange={handleChange}
                className="mb-4"
            />
            <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
            >
                {editingEvent ? "Save Changes" : "Add Event"}
            </button>

            {editingEvent && (
                <button
                    type="button"
                    onClick={onCancelEdit}
                    className="w-full mt-3 bg-gray-600 text-white font-bold p-3 rounded-lg hover:bg-gray-700"
                >
                    Cancel
                </button>
            )}
        </form>
    );
};

const EventList = ({ events, onDeleteEvent, onToggleHoliday, onEditEvent, companyId, token, userType }) => (
    <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto">
        <h3 className="text-xl font-bold text-gray-700 mb-4">Event Records</h3>
        <div className="w-full overflow-auto max-h-[70vh]">
            <table className="w-full min-w-max border-collapse bg-white rounded-lg shadow-md">
                <thead>
                    <tr className="bg-blue-600 text-white">
                        <th className="p-3 text-left font-semibold">Event Name</th>
                        <th className="p-3 text-left font-semibold">Event Date</th>
                        <th className="p-3 text-left font-semibold">Description</th>
                        <th className="p-3 text-left font-semibold">Holiday</th>
                        <th className="p-3 text-center font-semibold">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map((event) => (
                        <tr
                            key={event.id}
                            className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                        >
                            <td className="p-3 border-b border-gray-200">{event.eventName}</td>
                            <td className="p-3 border-b border-gray-200">
                                {new Date(event.eventDate).toLocaleDateString()}
                            </td>
                            <td className="p-3 border-b border-gray-200">{event.description}</td>
                            <td className="p-3 border-b border-gray-200">{event.isHoliday ? "Yes" : "No"}</td>
                            <td className="p-3 border-b border-gray-200 text-center">
                                <div className="flex flex-wrap justify-center gap-2">
                                    <button
                                        onClick={() => onEditEvent(event)}
                                        className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => onToggleHoliday(event.id, event, companyId, token, userType)}
                                        className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600"
                                    >
                                        Toggle Holiday
                                    </button>
                                    <button
                                        onClick={() => onDeleteEvent(event.id, companyId, token, userType)}
                                        className="bg-red-500 text-white px-3 py-1 rounded-lg hover:bg-red-600"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const CompanyEventsPage = () => {
    const { user, userType, companyId, logout } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);

    const fetchEvents = async () => {
        if (!companyId) {
            logout();
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const data = await EventService.getEvents(companyId, token, userType);
            setEvents(data);
            setError(null);
        } catch (err) {
            console.error("Error fetching events:", err);
            setError("Failed to fetch events. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEvent = async (id, companyId, token, userType) => {
        try {
            await EventService.deleteEvent(id, companyId, token, userType);
            setEvents((prevEvents) => 
                prevEvents.filter((event) => event.id !== id)
            );
            if (editingEvent?.id === id) {
                setEditingEvent(null);
            }
            setSuccess("Event deleted successfully!");
            setError(null);
        } catch (err) {
            console.error("Error deleting event:", err);
            setError("Failed to delete event. Please try again later.");
        }
    };

    const handleToggleHoliday = async (id, event, companyId, token, userType) => {
        try {
            const updatedEvent = { ...event, isHoliday: !event.isHoliday };
            await EventService.updateEvent(id, updatedEvent, companyId, token, userType);
            setEvents((prevEvents) =>
                prevEvents.map((e) => (e.id === id ? { ...e, isHoliday: !e.isHoliday } : e))
            );
            setSuccess("Event updated successfully!");
            setError(null);
        } catch (err) {
            console.error("Error updating event:", err);
            setError("Failed to update event. Please try again later.");
        }
    };

    const handleEditEvent = (event) => {
        setEditingEvent(event);
        setSuccess(null);
        setError(null);
    };

    const handleCancelEdit = () => {
        setEditingEvent(null);
    };

    useEffect(() => {
        fetchEvents();
    }, [companyId]);

    return (
        <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
            <h1 className="text-4xl font-bold text-white mb-6 text-center">Company Events and Holidays</h1>

            {companyId ? (
                <>
                    <EventForm 
                        onEventSaved={() => {
                            setEditingEvent(null);
                            fetchEvents();
                        }}
                        companyId={companyId}
                        token={localStorage.getItem('token')}
                        userType={userType}
                        editingEvent={editingEvent}
                        onCancelEdit={handleCancelEdit}
                    />

                    {loading ? (
                        <div className="text-center text-lg text-white">Loading...</div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg max-w-4xl mx-auto">
                            {error}
                        </div>
                    ) : (
                        <EventList
                            events={events}
                            onDeleteEvent={handleDeleteEvent}
                            onToggleHoliday={handleToggleHoliday}
                            onEditEvent={handleEditEvent}
                            companyId={companyId}
                            token={localStorage.getItem('token')}
                            userType={userType}
                        />
                    )}
                </>
            ) : (
                <div className="text-center text-lg text-white">
                    Company information not available. Please login again.
                </div>
            )}
        </div>
    );
};

export default CompanyEventsPage;